import { Banner, Box, InputDescription, InputLabel, Modal, TextInput } from '@fullstory/components';
import { intl } from '@fullstory/intl';
import { Visibility } from '@fullstory/proto/fs/gcloud/dstore/protoauditstore';
import { Dashboard } from '@fullstory/proto/fs/services/dx-objects';
import { int64 } from '@fullstory/proto/google/protobuf';
import { useFlags, useNotifications } from '@fullstory/react-hooks';
import {
  AppDispatch,
  DashboardRequestActions,
  fullstorySelectors,
  useGetAccess,
} from '@fullstory/redux';
import { FormikErrors } from 'formik';
import { isEmpty } from 'lodash';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { addToDashboardDeniedMessage } from '../../constants';
import { useAllDashboardTypes } from '../../hooks/useAllDashboardTypes';
import { WELCOME_DASHBOARD_ID } from '../../utils/constants';
import { validateRequiredField, validateRequiredInt64 } from '../../utils/validation';
import { DashboardSelectMenu } from '../DashboardSelectMenu';
import { AddToDashboardMetadata } from './types';

const { getOrgId } = fullstorySelectors;

const validateAddToDashboardForm = (
  metadata: AddToDashboardMetadata,
  showTitle: boolean,
  showDashboardTitle: boolean,
): FormikErrors<AddToDashboardMetadata> | undefined => {
  const formErrors: FormikErrors<AddToDashboardMetadata> = {};
  if (showTitle && validateRequiredField(metadata.title)) {
    formErrors.title = validateRequiredField(metadata.title) ?? undefined;
  }
  if (!showDashboardTitle && validateRequiredInt64(metadata.dashboardId)) {
    formErrors.dashboardId = validateRequiredInt64(metadata.dashboardId) ?? undefined;
  }
  if (showDashboardTitle && validateRequiredField(metadata.dashboardTitle)) {
    formErrors.dashboardTitle = validateRequiredField(metadata.dashboardTitle) ?? undefined;
  }
  return !isEmpty(formErrors) ? formErrors : undefined;
};

interface AddToDashboardModalProps {
  onCloseSignal: () => void;
  onSave: (metadata: AddToDashboardMetadata) => void;
  preventFetch?: boolean;
  saveText?: string;
  filterDashboards?: (value: Dashboard) => boolean;
  disableDashboards?: (value: Dashboard) => boolean;
  cardName?: string;
  showTitle: boolean;
  returnToDashboard: Dashboard | null;
  cardVisibility?: Visibility;
  objectVisibility?: Visibility;
}

export const AddToDashboardModal: React.FC<AddToDashboardModalProps> = ({
  onCloseSignal,
  onSave,
  preventFetch,
  filterDashboards,
  disableDashboards,
  saveText = intl.formatMessage({
    defaultMessage: 'Save to Dashboard',
    id: '0jQb+b',
    description: 'Save the card to the dashboard and close the modal popup',
  }),
  cardName,
  showTitle,
  returnToDashboard,
  cardVisibility,
  objectVisibility,
}) => {
  const intl = useIntl();
  const notifications = useNotifications();
  const orgId = useSelector(getOrgId);
  const flags = useFlags();
  const dispatch = useDispatch<AppDispatch>();
  const [selectedDashboard, setSelectedDashboard] = useState<Dashboard | null>(returnToDashboard);
  useEffect(() => {
    setSelectedDashboard(returnToDashboard);
  }, [returnToDashboard]);
  const { dashboardsLoading, ...dashboards } = useAllDashboardTypes(preventFetch);
  const getDashboardAccess = useGetAccess(DashboardRequestActions.getAccess, flags);

  const focusRef = useCallback((node: HTMLInputElement) => {
    if (node !== null) {
      node.focus();
    }
  }, []);

  const hasSaveableDashboards = useMemo(
    () =>
      dashboards.dashboards.filter(
        dashboard =>
          dashboard.id !== WELCOME_DASHBOARD_ID && (filterDashboards?.(dashboard) ?? true),
      ).length > 0,
    [dashboards, filterDashboards],
  );

  const [title, setTitle] = useState(cardName || '');
  const [dashboardTitle, setDashboardTitle] = useState('');
  const dashboardId = selectedDashboard?.id ?? int64.nil;
  const [showErrors, setShowErrors] = useState(false);
  // if we have no savable dashboards, show the title to create one on the fly
  const showDashboardTitle = !hasSaveableDashboards;

  const errors = useMemo(
    () =>
      validateAddToDashboardForm(
        {
          title,
          dashboardId,
          dashboardTitle,
        },
        showTitle,
        showDashboardTitle,
      ),
    [showTitle, title, dashboardTitle, dashboardId, showDashboardTitle],
  );
  const isValid = errors === undefined;

  const onSubmit = async () => {
    setShowErrors(true);
    if (!isValid) return;
    let updatedDashboardId = dashboardId;
    if (dashboardTitle) {
      try {
        // if dashboard title is set, we are creating a new dashboard to add card to.
        // The new dashboard should match the visibility of the object being added to it.
        const rsp = await dispatch(
          DashboardRequestActions.createDashboard(
            { name: dashboardTitle, visibility: objectVisibility },
            orgId,
            flags,
          ),
        );
        if (rsp.dashboard?.id) {
          updatedDashboardId = rsp.dashboard.id;
        }
      } catch (e) {
        notifications.error(
          intl.formatMessage({
            defaultMessage: 'Failed to create your Dashboard.',
            id: 'fvhx4c',
          }),
        );
      }
    }
    try {
      const access = await getDashboardAccess({
        int64Ids: { ids: [dashboardId] },
      });
      if (!access[dashboardId.toString()]?.canUpdate) {
        notifications.error(addToDashboardDeniedMessage);
        return;
      }
    } catch (e) {
      // swallow the error if this access check fails for some reason
      console.error('Error verifying dashboard access', dashboardId);
    }

    onSave({
      dashboardId: updatedDashboardId,
      title,
      dashboardTitle,
    });
  };

  // page flow and note cards should have an unset visibility which should not be considered a mismatch
  const mismatchVisibility =
    selectedDashboard && !!cardVisibility && selectedDashboard.visibility !== cardVisibility;

  return (
    <Modal data-component="AddToDashboardModal" onCloseSignal={onCloseSignal} disableBackdropClick>
      <Modal.Header onCloseSignal={onCloseSignal}>
        <FormattedMessage defaultMessage="Add to a Dashboard" id="/qThLU" />
      </Modal.Header>
      <Modal.Body>
        {showTitle && (
          <>
            <InputLabel htmlFor="dashboard-card-title">
              <FormattedMessage
                defaultMessage="Card Title"
                id="0HzQzr"
                description="The title of the card being added to the dashboard"
              />
            </InputLabel>
            <TextInput
              ref={focusRef}
              id="dashboard-card-title"
              placeholder={intl.formatMessage({
                defaultMessage: 'Give your card a title...',
                id: 'PUfIdb',
              })}
              value={title}
              onChange={e => setTitle(e.target.value)}
              invalid={showErrors && !!errors?.title}
            />
            {showErrors && !!errors?.title && (
              <InputDescription invalid description={errors.title} />
            )}
          </>
        )}
        {hasSaveableDashboards ? (
          <>
            <InputLabel htmlFor="dashboards-select-menu">
              <FormattedMessage
                defaultMessage="Save to"
                id="JkGT1U"
                description="Select the dashboard where the new card will be saved"
              />
            </InputLabel>
            <DashboardSelectMenu
              {...dashboards}
              id="dashboards-select-menu"
              dashboardsLoading={dashboardsLoading}
              filterDashboards={filterDashboards}
              selected={selectedDashboard}
              onChange={dashboard => setSelectedDashboard(dashboard)}
              disableDashboards={disableDashboards}
            />
            {showErrors && !!errors?.dashboardId && (
              <InputDescription invalid description={errors.dashboardId as string} />
            )}
          </>
        ) : (
          <>
            <InputLabel>Dashboard Title</InputLabel>
            <TextInput
              id="dashboard-title"
              placeholder={intl.formatMessage({
                defaultMessage: 'Give your Dashboard a title...',
                id: '0L4WdD',
              })}
              value={dashboardTitle}
              onChange={e => setDashboardTitle(e.target.value)}
              invalid={showErrors && !!errors?.dashboardTitle}
            />
            {showErrors && !!errors?.dashboardTitle && (
              <InputDescription invalid description={errors.dashboardTitle} />
            )}
          </>
        )}
        {mismatchVisibility && (
          <Box marginTop={24}>
            <Banner
              description={intl.formatMessage(
                {
                  defaultMessage: `The dashboard you're copying to is {visibility}. The copied object will match the visibility of the dashboard.`,
                  id: 'Ocxrra',
                },
                {
                  visibility:
                    selectedDashboard?.visibility === Visibility.PRIVATE ? 'private' : 'public',
                },
              )}
              variant="warning"
            />
          </Box>
        )}
      </Modal.Body>
      <Modal.FooterActions
        primaryAction={{ action: onSubmit, text: saveText }}
        secondaryAction={{
          action: onCloseSignal,
          text: intl.formatMessage({
            defaultMessage: 'Cancel',
            id: 'zl74I7',
            description: 'Cancel and close the modal popup',
          }),
        }}
      />
    </Modal>
  );
};
AddToDashboardModal.displayName = 'AddToDashboardModal';
